
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDButton from "components/MDButton";
import ButtonGroup from "@mui/material/ButtonGroup";
import CustomAlert from "components/CustomAlert";
import MDInput from "components/MDInput";
import CircularProgress from '@mui/material/CircularProgress';
import ProductionLineSkeleton from "components/ProductionLineSkeleton";

// Data
import { useEffect, useState } from "react";
import { ProductionLineAPI } from "api/ProductionLineAPI";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useMaterialUIController, setLoading } from "context";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup'


export default function CreateEditProductionLine() {
  const [controller, dispatch] = useMaterialUIController();
  const [alert, setAlert] = useState(false);
  const [alertContent, setAlertContent] = useState('');
  const [alertColor, setAlertColor] = useState('info');
  const navigate = useNavigate();
  const { loading } = controller;

  const { id } = useParams();
  const isCreateMode = !id;

  const formSchema = yup.object().shape({
    linea: yup.string().required('El campo es requerido').max(30, 'El código no debe tener más de 30 caracteres'),
    nombre_linea: yup.string().required('El campo es requerido').max(30, 'El código no debe tener más de 30 caracteres'),
    oee_historico: yup.number().required('El campo es requerido'),
    td_semana: yup.number().required('El campo es requerido'),
    rt: yup.number().required('El campo es requerido'),
    r_calculado: yup.number().required('El campo es requerido'),
    r: yup.number().required('El campo es requerido'),
  })

  const { handleSubmit, control, formState: { errors, isSubmitting }, setValue } = useForm({ resolver: yupResolver(formSchema), });

  const handleAlert = (content, color) => {
    setAlertContent(content);
    setAlertColor(color);
    setAlert(true)
  }

  const handleAlertClose = () => {
    setAlert(false);
  }

  const onSubmit = (data) => {
    return isCreateMode
      ? createProductionLine(data)
      : updateProductionLine(id, data);
  }

  const createProductionLine = (data) => {
    return ProductionLineAPI.store(data)
      .then(response => {
        navigate('/production-lines', { state: response.message });

      }).catch(error => {
        if (error.response) {
          handleAlert(error.response.data.message, 'error')
          window.scrollTo(0, 0);
        }
      });
  }

  const updateProductionLine = (id, data) => {
    return ProductionLineAPI.update(id, data)
      .then(response => {
        navigate('/production-lines', { state: response.message });

      }).catch(error => {
        if (error.response) {
          handleAlert(error.response.data.message, 'error')
          window.scrollTo(0, 0);
        }
      });
  }

  const getProductionLine = (id, signal) => {
    return ProductionLineAPI.show(id, signal).then(response => {
      for (let [key, value] of Object.entries(response.productionLine)) {
        setValue(key, value)
      }
    }).catch(error => {
      navigate('/not-found');
    }).finally(() => {
      setLoading(dispatch, false)
    });
  }

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal

    if (!isCreateMode) {
      setLoading(dispatch, true)
      getProductionLine(id, signal);
    }

    return () => {
      abortController.abort();
    };

  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid container justifyContent="center" pb={3} >
        <MDBox sx={{
          width: '80%',
        }}>
          <MDBox mb={4} px={2} >
            <CustomAlert severity={alertColor} message={alertContent} open={alert} onClose={handleAlertClose}></CustomAlert>
          </MDBox>
          <Card>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={12}>
                <MDBox p={2} lineHeight={0}>
                  <MDTypography variant="h5">Lineas de Producto</MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Información de la Linea de Producto
                  </MDTypography>
                </MDBox>
                {loading ? <ProductionLineSkeleton></ProductionLineSkeleton> : (
                  <MDBox p={2} component="form" role="form" onSubmit={handleSubmit(onSubmit)}>
                    <Grid container p={2} spacing={3}>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name="linea"
                          render={({ field: { onChange, value } }) => (
                            <MDInput onChange={onChange} value={value || ''} label="Linea" variant="standard" fullWidth />
                          )}
                        />
                        {errors.linea && <MDTypography variant="button" color="error">{errors.linea?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name="nombre_linea"
                          render={({ field: { onChange, value } }) => (
                            <MDInput onChange={onChange} value={value || ''} label="Nombre Linea" variant="standard" fullWidth />
                          )}
                        />
                        {errors.nombre_linea && <MDTypography variant="button" color="error">{errors.nombre_linea?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name="oee_historico"
                          render={({ field: { onChange, value } }) => (
                            <MDInput type="number" onChange={onChange} value={value || ''} label="OEE Histórico" variant="standard" fullWidth />
                          )}
                        />
                        {errors.oee_historico && <MDTypography variant="button" color="error">{errors.oee_historico?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name="td_semana"
                          render={({ field: { onChange, value } }) => (
                            <MDInput type="number" onChange={onChange} value={value || ''} label="TD Semana" variant="standard" fullWidth />
                          )}
                        />
                        {errors.td_semana && <MDTypography variant="button" color="error">{errors.td_semana?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name="rt"
                          render={({ field: { onChange, value } }) => (
                            <MDInput type="number" onChange={onChange} value={value || ''} label="RT" variant="standard" fullWidth />
                          )}
                        />
                        {errors.rt && <MDTypography variant="button" color="error">{errors.rt?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name="r_calculado"
                          render={({ field: { onChange, value } }) => (
                            <MDInput type="number" onChange={onChange} value={value || ''} label="R Calculado" variant="standard" fullWidth />
                          )}
                        />
                        {errors.r_calculado && <MDTypography variant="button" color="error">{errors.r_calculado?.message}</MDTypography>}
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <Controller
                          control={control}
                          name="r"
                          render={({ field: { onChange, value } }) => (
                            <MDInput type="number" onChange={onChange} value={value || ''} label="R" variant="standard" fullWidth />
                          )}
                        />
                        {errors.r && <MDTypography variant="button" color="error">{errors.r?.message}</MDTypography>}
                      </Grid>
                    </Grid>
                    <MDBox sx={{ display: 'flex', justifyContent: 'flex-end' }} p={5}>
                      <ButtonGroup>
                        <MDButton variant="gradient" color="secondary" onClick={() => navigate('/production-lines')} >
                          Cancelar
                        </MDButton>
                        <MDButton variant="gradient" type="submit" color="info" disabled={isSubmitting}>
                          {isSubmitting ? <CircularProgress size={18} color="white" /> : 'Guardar'}
                        </MDButton>
                      </ButtonGroup>
                    </MDBox>
                  </MDBox>
                )}
              </Grid>
            </Grid>
          </Card>
        </MDBox>
      </Grid >
      <Footer />
    </DashboardLayout >
  );

}



